import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "./Dashboard";
import { Modal, Field, Th, Td } from "./Customers";
import { Plus, X, Check, CircleSlash, Receipt, BadgeDollarSign, Sparkles, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_STYLES = {
  quotation: "bg-white text-[#0A0A0A] border-[#0A0A0A]",
  confirmed: "bg-[#0033A0] text-white border-[#0033A0]",
  invoiced: "bg-[#F5B700] text-[#0A0A0A] border-[#F5B700]",
  paid: "bg-[#0B7A75] text-white border-[#0B7A75]",
  cancelled: "bg-[#495057] text-white border-[#495057]",
};

export default function Sales() {
  const { tenant } = useAuth();
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null); // detail order
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/sales/orders").then(({ data }) => setOrders(data));
  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1400px]" data-testid="sales-page">
      <PageHeader
        title="Sales"
        subtitle="Quotation → Confirmed → Invoiced → Paid. Stock decrements & AR posts on invoice automatically."
        action={
          <button onClick={() => setOpen(true)} data-testid="new-order-btn"
            className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm font-medium rounded-sm transition-colors">
            <Plus size={14} /> New quotation
          </button>
        }
      />

      <div className="mt-6 flex flex-wrap gap-2" data-testid="status-filter">
        {["all", "quotation", "confirmed", "invoiced", "paid", "cancelled"].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            data-testid={`filter-${s}`}
            className={`text-xs uppercase tracking-[0.15em] px-3 py-1.5 border ${filter === s ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#DEE2E6] text-[#495057]"}`}>
            {s}
          </button>
        ))}
      </div>

      <div className="mt-6 border border-[#0A0A0A] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
            <tr>
              <Th>Number</Th><Th>Customer</Th><Th>Status</Th><Th align="right">Total</Th><Th align="right">Paid</Th><Th>Date</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((o, i) => (
              <tr key={o.id} data-testid={`order-row-${i}`} onClick={() => setView(o)}
                className="border-b border-[#DEE2E6] hover:bg-[#F8F9FA] cursor-pointer">
                <Td className="font-mono text-xs font-semibold">{o.number}</Td>
                <Td>{o.customer_name}</Td>
                <Td><StatusBadge status={o.status} /></Td>
                <Td align="right" className="tabular-nums">{tenant?.currency || ""} {o.total.toLocaleString()}</Td>
                <Td align="right" className="tabular-nums text-[#0B7A75]">{(o.paid_amount || 0).toLocaleString()}</Td>
                <Td className="text-[#495057]">{new Date(o.created_at).toLocaleDateString()}</Td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#495057]">No orders.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && <NewOrderForm onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {view && <OrderDetail order={view} onClose={() => setView(null)} onChanged={() => { setView(null); load(); }} />}
    </div>
  );
}

export function StatusBadge({ status }) {
  return (
    <span data-testid={`status-${status}`} className={`inline-block text-[10px] uppercase tracking-[0.2em] border px-2 py-0.5 ${STATUS_STYLES[status]}`}>
      {status}
    </span>
  );
}

function NewOrderForm({ onClose, onSaved }) {
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [customer, setCustomer] = useState("");
  const [warehouse, setWarehouse] = useState("");
  const [taxPct, setTaxPct] = useState(15);
  const [lines, setLines] = useState([{ item_id: "", qty: 1, unit_price: 0, discount_pct: 0 }]);
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/customers"), api.get("/items"), api.get("/warehouses")])
      .then(([c, i, w]) => { setCustomers(c.data); setItems(i.data); setWarehouses(w.data); });
  }, []);

  const totals = useMemo(() => {
    const sub = lines.reduce((s, l) => {
      const it = items.find((x) => x.id === l.item_id);
      const price = parseFloat(l.unit_price || (it?.price ?? 0));
      const q = parseFloat(l.qty || 0);
      const d = parseFloat(l.discount_pct || 0);
      return s + q * price * (1 - d / 100);
    }, 0);
    const tax = sub * (parseFloat(taxPct || 0) / 100);
    return { sub: round2(sub), tax: round2(tax), total: round2(sub + tax) };
  }, [lines, items, taxPct]);

  const addLine = () => setLines((l) => [...l, { item_id: "", qty: 1, unit_price: 0, discount_pct: 0 }]);
  const removeLine = (i) => setLines((l) => l.filter((_, idx) => idx !== i));
  const updateLine = (i, patch) => setLines((l) => l.map((ln, idx) => (idx === i ? { ...ln, ...patch } : ln)));

  const onItemChange = (i, item_id) => {
    const it = items.find((x) => x.id === item_id);
    updateLine(i, { item_id, unit_price: it?.price ?? 0 });
  };

  const save = async (e) => {
    e.preventDefault();
    setErr(""); setSaving(true);
    try {
      const payload = {
        customer_id: customer,
        warehouse_id: warehouse || null,
        tax_pct: parseFloat(taxPct || 0),
        notes,
        lines: lines.filter((l) => l.item_id && l.qty > 0).map((l) => ({
          item_id: l.item_id,
          qty: parseFloat(l.qty),
          unit_price: parseFloat(l.unit_price),
          discount_pct: parseFloat(l.discount_pct || 0),
        })),
      };
      if (!payload.customer_id) throw new Error("Pick a customer");
      if (payload.lines.length === 0) throw new Error("Add at least one line");
      await api.post("/sales/orders", payload);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Modal title="New quotation" onClose={onClose} testid="new-order-modal" wide>
      <form onSubmit={save} className="space-y-5" data-testid="new-order-form">
        <div className="grid grid-cols-2 gap-4">
          <Select label="Customer" required value={customer} onChange={setCustomer} testid="order-customer"
            options={customers.map((c) => ({ value: c.id, label: c.name }))} />
          <Select label="Warehouse" value={warehouse} onChange={setWarehouse} testid="order-warehouse"
            options={[{ value: "", label: "— Any —" }, ...warehouses.map((w) => ({ value: w.id, label: `${w.code} · ${w.name}` }))]} />
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-[#495057] mb-2">Lines</div>
          <div className="border border-[#DEE2E6]">
            <div className="grid grid-cols-[1fr_80px_120px_80px_100px_30px] gap-2 px-3 py-2 bg-[#F8F9FA] text-[10px] uppercase tracking-[0.15em] text-[#495057]">
              <div>Item</div><div className="text-right">Qty</div><div className="text-right">Unit price</div><div className="text-right">Disc %</div><div className="text-right">Total</div><div></div>
            </div>
            {lines.map((ln, i) => {
              const it = items.find((x) => x.id === ln.item_id);
              const total = (parseFloat(ln.qty || 0) * parseFloat(ln.unit_price || 0) * (1 - parseFloat(ln.discount_pct || 0) / 100));
              return (
                <div key={i} data-testid={`order-line-${i}`} className="grid grid-cols-[1fr_80px_120px_80px_100px_30px] gap-2 px-3 py-2 border-t border-[#DEE2E6] items-center">
                  <select required value={ln.item_id} onChange={(e) => onItemChange(i, e.target.value)} data-testid={`order-line-item-${i}`}
                    className="bg-white border border-[#DEE2E6] px-2 py-1.5 text-sm">
                    <option value="">— pick item —</option>
                    {items.map((x) => <option key={x.id} value={x.id}>{x.sku} · {x.name} (stock {x.stock_on_hand})</option>)}
                  </select>
                  <input type="number" min="0.01" step="0.01" required value={ln.qty} onChange={(e) => updateLine(i, { qty: e.target.value })}
                    data-testid={`order-line-qty-${i}`}
                    className="bg-white border border-[#DEE2E6] px-2 py-1.5 text-sm text-right" />
                  <input type="number" min="0" step="0.01" required value={ln.unit_price} onChange={(e) => updateLine(i, { unit_price: e.target.value })}
                    data-testid={`order-line-price-${i}`}
                    className="bg-white border border-[#DEE2E6] px-2 py-1.5 text-sm text-right" />
                  <input type="number" min="0" max="100" step="0.5" value={ln.discount_pct} onChange={(e) => updateLine(i, { discount_pct: e.target.value })}
                    data-testid={`order-line-discount-${i}`}
                    className="bg-white border border-[#DEE2E6] px-2 py-1.5 text-sm text-right" />
                  <div className="text-right text-sm tabular-nums">{round2(total).toLocaleString()}</div>
                  <button type="button" onClick={() => removeLine(i)} data-testid={`order-line-remove-${i}`}
                    className="text-[#D90429] hover:bg-[#D90429]/10 p-1"><X size={14} /></button>
                </div>
              );
            })}
            <button type="button" onClick={addLine} data-testid="order-add-line"
              className="w-full px-3 py-2 text-xs text-[#0033A0] hover:bg-[#F8F9FA] border-t border-[#DEE2E6] text-left">+ Add line</button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Tax %" type="number" value={taxPct} onChange={setTaxPct} testid="order-tax" />
          <div className="border border-[#DEE2E6] bg-[#F8F9FA] p-3 text-sm">
            <div className="flex justify-between py-0.5"><span className="text-[#495057]">Subtotal</span><span className="tabular-nums">{totals.sub.toLocaleString()}</span></div>
            <div className="flex justify-between py-0.5"><span className="text-[#495057]">Tax ({taxPct}%)</span><span className="tabular-nums">{totals.tax.toLocaleString()}</span></div>
            <div className="flex justify-between py-1.5 border-t border-[#DEE2E6] mt-1 font-bold"><span>Total</span><span className="tabular-nums" data-testid="order-total">{totals.total.toLocaleString()}</span></div>
          </div>
        </div>

        <Field label="Notes" value={notes} onChange={setNotes} testid="order-notes" />
        {err && <div className="text-sm text-[#D90429] border border-[#D90429] bg-[#D90429]/5 px-3 py-2">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button>
          <button type="submit" disabled={saving} data-testid="order-save"
            className="px-4 py-2 text-sm bg-[#0A0A0A] hover:bg-[#FF4F00] text-white">Save quotation</button>
        </div>
      </form>
    </Modal>
  );
}

function OrderDetail({ order: initial, onClose, onChanged }) {
  const { tenant } = useAuth();
  const [order, setOrder] = useState(initial);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [momoOpen, setMomoOpen] = useState(false);

  const refresh = async () => {
    const { data } = await api.get(`/sales/orders/${order.id}`);
    setOrder(data);
  };

  const action = async (path) => {
    setBusy(true); setErr("");
    try { await api.post(`/sales/orders/${order.id}/${path}`); await refresh(); onChanged?.(); }
    catch (e) { setErr(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`${order.number} · ${order.customer_name}`} onClose={onClose} testid="order-detail-modal" wide>
      <div className="space-y-5" data-testid="order-detail">
        <div className="flex flex-wrap gap-3 items-center">
          <StatusBadge status={order.status} />
          <span className="text-xs text-[#495057]">Created {new Date(order.created_at).toLocaleString()}</span>
          {order.invoice_number && <span className="text-xs text-[#0033A0]">Invoice {order.invoice_number}</span>}
        </div>

        <div className="border border-[#DEE2E6]">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FA] text-[10px] uppercase tracking-[0.15em] text-[#495057]">
              <tr><Th>SKU</Th><Th>Name</Th><Th align="right">Qty</Th><Th align="right">Unit</Th><Th align="right">Disc</Th><Th align="right">Line total</Th></tr>
            </thead>
            <tbody>
              {order.lines.map((l, i) => (
                <tr key={i} className="border-t border-[#DEE2E6]">
                  <Td className="font-mono text-xs">{l.sku}</Td>
                  <Td>{l.name}</Td>
                  <Td align="right" className="tabular-nums">{l.qty}</Td>
                  <Td align="right" className="tabular-nums">{l.unit_price.toLocaleString()}</Td>
                  <Td align="right" className="tabular-nums">{l.discount_pct}%</Td>
                  <Td align="right" className="tabular-nums">{round2(l.qty * l.unit_price * (1 - l.discount_pct / 100)).toLocaleString()}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="text-[#495057]">Notes: {order.notes || "—"}</div>
          <div className="border border-[#DEE2E6] bg-[#F8F9FA] p-3">
            <Row label="Subtotal" v={`${tenant?.currency || ""} ${order.subtotal.toLocaleString()}`} />
            <Row label={`Tax (${order.tax_pct}%)`} v={`${tenant?.currency || ""} ${order.tax_amount.toLocaleString()}`} />
            <Row label="Total" v={`${tenant?.currency || ""} ${order.total.toLocaleString()}`} bold />
            <Row label="Paid" v={`${tenant?.currency || ""} ${(order.paid_amount || 0).toLocaleString()}`} green />
            <Row label="Outstanding" v={`${tenant?.currency || ""} ${round2(order.total - (order.paid_amount || 0)).toLocaleString()}`} red />
          </div>
        </div>

        {err && <div className="text-sm text-[#D90429] border border-[#D90429] bg-[#D90429]/5 px-3 py-2">{err}</div>}

        <div className="flex flex-wrap gap-2 justify-end pt-2 border-t border-[#DEE2E6]">
          {order.status === "quotation" && (
            <>
              <button onClick={() => action("cancel")} disabled={busy} data-testid="order-cancel-btn"
                className="px-4 py-2 text-sm border border-[#D90429] text-[#D90429] hover:bg-[#D90429] hover:text-white inline-flex items-center gap-1.5"><CircleSlash size={14} /> Cancel</button>
              <button onClick={() => action("confirm")} disabled={busy} data-testid="order-confirm-btn"
                className="px-4 py-2 text-sm bg-[#0033A0] text-white hover:bg-[#001F66] inline-flex items-center gap-1.5"><Check size={14} /> Confirm order</button>
              <button onClick={() => action("invoice")} disabled={busy} data-testid="order-invoice-btn"
                className="px-4 py-2 text-sm bg-[#F5B700] text-[#0A0A0A] hover:bg-[#D9A400] inline-flex items-center gap-1.5"><Receipt size={14} /> Invoice now</button>
            </>
          )}
          {order.status === "confirmed" && (
            <button onClick={() => action("invoice")} disabled={busy} data-testid="order-invoice-btn"
              className="px-4 py-2 text-sm bg-[#F5B700] text-[#0A0A0A] hover:bg-[#D9A400] inline-flex items-center gap-1.5"><Receipt size={14} /> Invoice</button>
          )}
          {order.status === "invoiced" && (
            <>
              <button onClick={() => setPayOpen(true)} disabled={busy} data-testid="order-record-payment-btn"
                className="px-4 py-2 text-sm bg-[#0B7A75] text-white hover:bg-[#085c58] inline-flex items-center gap-1.5"><BadgeDollarSign size={14} /> Record payment</button>
              <button onClick={() => setMomoOpen(true)} disabled={busy} data-testid="order-momo-btn"
                className="px-4 py-2 text-sm bg-[#FFCC00] text-[#0A0A0A] hover:bg-[#F5B700] inline-flex items-center gap-1.5"><Sparkles size={14} /> Pay via MTN MoMo</button>
            </>
          )}
        </div>
      </div>

      {payOpen && (
        <PaymentForm
          order={order}
          onClose={() => setPayOpen(false)}
          onSaved={async () => { setPayOpen(false); await refresh(); onChanged?.(); }}
        />
      )}
      {momoOpen && (
        <MoMoForm
          order={order}
          onClose={() => setMomoOpen(false)}
          onSaved={async () => { setMomoOpen(false); await refresh(); onChanged?.(); }}
        />
      )}
    </Modal>
  );
}

function PaymentForm({ order, onClose, onSaved }) {
  const outstanding = round2(order.total - (order.paid_amount || 0));
  const [amount, setAmount] = useState(outstanding);
  const [method, setMethod] = useState("bank_transfer");
  const [reference, setReference] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setErr(""); setSaving(true);
    try {
      await api.post("/sales/payments", {
        sales_order_id: order.id, amount: parseFloat(amount), method, reference,
      });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={`Record payment · ${order.number}`} onClose={onClose} testid="payment-modal">
      <form onSubmit={save} className="space-y-4" data-testid="payment-form">
        <Field label={`Amount (outstanding: ${outstanding.toLocaleString()})`} type="number" required value={amount} onChange={setAmount} testid="payment-amount" />
        <Select label="Method" value={method} onChange={setMethod} testid="payment-method"
          options={[
            { value: "bank_transfer", label: "Bank transfer" },
            { value: "mobile_money", label: "Mobile money" },
            { value: "cash", label: "Cash" },
            { value: "cheque", label: "Cheque" },
            { value: "card", label: "Card" },
          ]} />
        <Field label="Reference" value={reference} onChange={setReference} testid="payment-reference" />
        {err && <div className="text-sm text-[#D90429]">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button>
          <button type="submit" disabled={saving} data-testid="payment-save"
            className="px-4 py-2 text-sm bg-[#0B7A75] text-white hover:bg-[#085c58]">Record</button>
        </div>
      </form>
    </Modal>
  );
}

function Select({ label, value, onChange, options, required, testid }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">{label}</span>
      <select required={required} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm focus:border-[#0A0A0A] focus:outline-none">
        {!required && <option value="">— select —</option>}
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function Row({ label, v, bold, green, red }) {
  const cls = bold ? "font-bold border-t border-[#DEE2E6] pt-1.5 mt-1" : "";
  const vCls = green ? "text-[#0B7A75]" : red ? "text-[#D90429]" : "";
  return (
    <div className={`flex justify-between py-0.5 ${cls}`}>
      <span className="text-[#495057]">{label}</span>
      <span className={`tabular-nums ${vCls}`}>{v}</span>
    </div>
  );
}

function round2(n) { return Math.round(n * 100) / 100; }


/* ─── MTN MoMo payment ────────────────────────────────────────────── */
function MoMoForm({ order, onClose, onSaved }) {
  const outstanding = round2(order.total - (order.paid_amount || 0));
  const [msisdn, setMsisdn] = useState("");
  const [amount, setAmount] = useState(outstanding);
  const [phase, setPhase] = useState("input");  // input | pending | success | failed
  const [txn, setTxn] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState(null);

  useEffect(() => { api.get("/momo/config").then(({ data }) => setMode(data.mode)).catch(() => {}); }, []);

  const initiate = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const { data } = await api.post("/momo/request-to-pay", {
        sales_order_id: order.id,
        invoice_number: order.invoice_number || order.number,
        msisdn, amount: parseFloat(amount),
        currency: "EUR",  // Sandbox limitation — display we show GHS, MTN sandbox uses EUR
        payer_message: `Gr8Boost ERP · Invoice ${order.invoice_number || order.number}`,
        payee_note: "Gr8Boost ERP",
      });
      setTxn(data); setPhase("pending");
      // Begin polling
      const poll = async (attempts = 0) => {
        if (attempts > 20) { setPhase("failed"); setErr("Timed out waiting for MoMo confirmation."); return; }
        const { data: st } = await api.get(`/momo/status/${data.transaction_id}`);
        setTxn(st);
        if (st.status === "SUCCESSFUL") { setPhase("success"); await onSavedSafe(); return; }
        if (st.status === "FAILED") { setPhase("failed"); setErr(st.reason || "Customer declined the prompt."); return; }
        setTimeout(() => poll(attempts + 1), 2500);
      };
      setTimeout(() => poll(0), 1200);
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
  };
  const onSavedSafe = async () => { setTimeout(() => onSaved(), 1500); };

  return (
    <Modal title={`Pay via MTN MoMo · ${order.invoice_number || order.number}`} onClose={onClose} testid="momo-modal">
      {phase === "input" && (
        <form onSubmit={initiate} className="space-y-4" data-testid="momo-form">
          <div className="text-xs text-[#495057] bg-[#FFF8E1] border border-[#F5B700]/40 px-3 py-2">
            MTN MoMo {mode === "mock" ? "(MOCK MODE — wire MOMO_SUBSCRIPTION_KEY env to go live sandbox)" : `(${mode || "loading..."})`}
            <div className="mt-1 text-[10px]">Test MSISDNs in MOCK: <span className="font-mono">…0</span> = pending then SUCCESS · <span className="font-mono">…1</span> = FAILED · any other = instant SUCCESS</div>
          </div>
          <Field label="Customer MSISDN (e.g. 233241234567)" required value={msisdn} onChange={setMsisdn} testid="momo-msisdn" />
          <Field label="Amount (GHS)" type="number" required value={amount} onChange={setAmount} testid="momo-amount" />
          {err && <div className="text-sm text-[#D90429]" data-testid="momo-error">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button>
            <button type="submit" data-testid="momo-submit" className="px-4 py-2 text-sm bg-[#FFCC00] hover:bg-[#F5B700] text-[#0A0A0A] inline-flex items-center gap-1.5"><Sparkles size={14} /> Send prompt</button>
          </div>
        </form>
      )}
      {phase === "pending" && (
        <div className="text-center py-6 space-y-3" data-testid="momo-pending">
          <Loader2 className="mx-auto animate-spin text-[#FF4F00]" size={36} />
          <div className="font-bold">Waiting for MoMo confirmation…</div>
          <div className="text-xs text-[#495057]">Customer should approve the prompt on their phone. Ref: <span className="font-mono">{txn?.transaction_id?.slice(0, 8)}</span></div>
        </div>
      )}
      {phase === "success" && (
        <div className="text-center py-6 space-y-3" data-testid="momo-success">
          <div className="w-12 h-12 mx-auto rounded-full bg-[#0B7A75] text-white grid place-items-center"><Check size={24} /></div>
          <div className="font-bold text-[#0B7A75]">Payment confirmed</div>
          <div className="text-xs text-[#495057]">MoMo settled. Order updated, AR reduced, journal posted automatically.</div>
        </div>
      )}
      {phase === "failed" && (
        <div className="text-center py-6 space-y-3" data-testid="momo-failed">
          <div className="w-12 h-12 mx-auto rounded-full bg-[#D90429] text-white grid place-items-center"><X size={24} /></div>
          <div className="font-bold text-[#D90429]">Payment failed</div>
          <div className="text-xs text-[#495057]">{err}</div>
          <button onClick={() => { setPhase("input"); setErr(""); }} className="px-4 py-2 text-sm border border-[#0A0A0A]">Try again</button>
        </div>
      )}
    </Modal>
  );
}
